import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchComments,
  addComment,
  editComment,
  removeComment,
  toggleLikeComment,
} from '../../store/slices/comment.slice'
import { fetchUsers } from '../../store/slices/user.slice'
import { CommentList } from './CommentList'
import toast from 'react-hot-toast'
import { openNotRegisteredPopup } from '../../store/slices/ui.slice'
import { Button } from '../ui/elements'

export function CommentIndex({ postId, loggedInUser }) {
  const dispatch = useDispatch()

  // שליפת התגובות והמשתמשים מהסטור
  const comments = useSelector((state) => state.commentModule.comments)
  const users = useSelector((state) => state.userModule.users)

  // מזער (memoize) את סינון התגובות של ה־postId
  const filteredComments = useMemo(() => {
    return comments.filter((comment) => comment.postId === postId)
  }, [comments, postId])

  // זיהוי מזהי המשתמשים הרלוונטיים לתגובות
  const relevantUserIds = useMemo(() => {
    const userIds = filteredComments.map((comment) => comment.userId)
    return [...new Set(userIds)] // מסיר כפילויות
  }, [filteredComments])

  // State עבור תוכן התגובה החדשה (ראשית)
  const [newCommentContent, setNewCommentContent] = useState('')

  useEffect(() => {
    if (postId) {
      dispatch(fetchComments(postId))
    }
  }, [postId, dispatch]) // מאזין רק ל-postId ול-dispatch

  useEffect(() => {
    if (relevantUserIds.length) {
      dispatch(fetchUsers({ userIds: relevantUserIds }))
    }
  }, [relevantUserIds, dispatch])

  async function handleAddComment(content, parentCommentId = null) {
    if (!loggedInUser) {
      toast.error('עליך להתחבר כדי להוסיף תגובה.')
      return
    }

    const newComment = {
      postId,
      parentCommentId,
      userId: loggedInUser._id,
      content,
      createdAt: new Date().toISOString(),
      likes: [],
    }

    try {
      const result = await dispatch(
        addComment({ commentData: newComment, loggedInUser })
      ).unwrap()
      toast.success('תגובה נוספה בהצלחה!')
    } catch (err) {
      console.error('Error adding comment:', err)
      toast.error('שגיאה בהוספת תגובה.')
    }
  }

  async function handleEditComment(commentId, updatedContent) {
    if (!commentId || !updatedContent.trim()) {
      toast.error('תוכן או מזהה תגובה חסרים.')
      return
    }

    try {
      const result = await dispatch(
        editComment({
          commentId,
          updatedData: { content: updatedContent },
          loggedInUser,
        })
      ).unwrap()
      toast.success('התגובה נערכה בהצלחה!')
    } catch (err) {
      console.error('Error editing comment:', err)
      toast.error('שגיאה בעריכת התגובה.')
    }
  }

  async function handleRemoveComment(commentId) {
    if (!commentId) {
      toast.error('מזהה תגובה חסר.')
      return
    }

    try {
      const result = await dispatch(
        removeComment({ commentId, loggedInUser })
      ).unwrap()
      toast.success('התגובה נמחקה בהצלחה!')
    } catch (err) {
      console.error('Error removing comment:', err)
      toast.error('שגיאה במחיקת התגובה.')
    }
  }

  async function handleToggleLike(commentId) {
    if (!loggedInUser) {
      toast.error('עליך להתחבר כדי לעשות לייק.')
      dispatch(openNotRegisteredPopup())
      return
    }

    try {
      // שולח את loggedInUser (מהפונקציה, רק ה־_id יישלח ל-backend)
      await dispatch(toggleLikeComment({ commentId, loggedInUser })).unwrap()
      toast.success('לייק עודכן בהצלחה!')
    } catch (err) {
      console.error('❌ Error toggling like:', err)
      toast.error('שגיאה בעת לייק/הסרת לייק.')
    }
  }

  // הוספת תגובה ראשית (אין parentCommentId)
  function onSubmitNewComment() {
    if (!newCommentContent.trim()) {
      return
    }
    handleAddComment(newCommentContent, null)
    setNewCommentContent('')
  }

  return (
    <section className="container mx-auto mt-12">
      <h3 className="text-xl mb-4">תגובות ({filteredComments.length})</h3>

      {/* הצגת אינפוט (טקסט אריה) תמיד להוספת תגובה חדשה ראשית */}
      <div className="mb-4 p-4">
        <textarea
          rows={3}
          className={`w-full p-2 border border-gray-600 rounded bg-secondaryBackground text-white ${
            !loggedInUser ? 'cursor-not-allowed' : ''
          }`}
          placeholder={
            loggedInUser
              ? 'הוסף תגובה חדשה...'
              : 'רק משתמשים מחוברים יכולים להגיב.'
          }
          value={newCommentContent}
          onChange={(e) => setNewCommentContent(e.target.value)}
          disabled={!loggedInUser} // נועל את ה-textarea אם המשתמש לא מחובר
        ></textarea>

        <div className="flex justify-end mt-2">
          <Button
            text="הוסף תגובה"
            width={140}
            height={60}
            onClick={onSubmitNewComment}
            disabled={!newCommentContent.trim()}
            fontSize="text-lg"
            textColor="text-white"
            bgColor="fill-primary"
            hoverBgColor="hover:scale-105 transform duration-300"
            aria-label="הוסף תגובה לכתבה"
            title="הוסף תגובה"
          />
        </div>
      </div>

      {/* הצגת רשימת התגובות (מסוננות לפי postId) */}
      <CommentList
        comments={filteredComments}
        users={users}
        loggedInUser={loggedInUser}
        onAddComment={handleAddComment}
        onEditComment={handleEditComment}
        onRemoveComment={handleRemoveComment}
        onToggleLike={handleToggleLike}
      />
    </section>
  )
}
